import { HeroSlideForm } from '@/components/hero-slides/HeroSlideForm'
import { HeroSlideList } from '@/components/hero-slides/HeroSlideList'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { HeroSlide, HeroSlideFormData } from '@/types/api'
import React, { useEffect, useState } from 'react'

const HeroSlidesPage: React.FC = () => {
  const { toast } = useToast()
  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingSlide, setEditingSlide] = useState<HeroSlide | undefined>(undefined)
  const [slideToDelete, setSlideToDelete] = useState<HeroSlide | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const loadHeroSlides = React.useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.getHeroSlides()
      setHeroSlides(response.data || [])
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load hero slides',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  useEffect(() => {
    loadHeroSlides()
  }, [loadHeroSlides])

  const handleAddSlide = () => {
    setEditingSlide(undefined)
    setShowForm(true)
  }

  const handleEditSlide = (slide: HeroSlide) => {
    setEditingSlide(slide)
    setShowForm(true)
  }

  const handleFormSubmit = async (data: HeroSlideFormData) => {
    setIsSubmitting(true)
    try {
      if (editingSlide) {
        await apiClient.updateHeroSlide(editingSlide.id, data)
        toast({ title: 'Success', description: 'Hero slide updated successfully' })
      } else {
        await apiClient.createHeroSlide(data)
        toast({ title: 'Success', description: 'Hero slide created successfully' })
      }
      setShowForm(false)
      setEditingSlide(undefined)
      loadHeroSlides()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save hero slide',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteSlide = (slideId: string) => {
    const slide = heroSlides.find((s) => s.id === slideId)
    if (slide) setSlideToDelete(slide)
  }

  const confirmDelete = async () => {
    if (!slideToDelete) return
    try {
      await apiClient.deleteHeroSlide(slideToDelete.id)
      toast({ title: 'Success', description: 'Hero slide deleted successfully' })
      setSlideToDelete(null)
      loadHeroSlides()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete hero slide',
        variant: 'destructive',
      })
    }
  }

  const handleToggleStatus = async (slideId: string) => {
    try {
      await apiClient.toggleHeroSlideStatus(slideId)
      toast({ title: 'Success', description: 'Hero slide status updated' })
      loadHeroSlides()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Hero Slides</h1>
        <p className="text-muted-foreground">Manage homepage hero slider content and settings</p>
      </div>

      {showForm ? (
        <HeroSlideForm
          initialData={editingSlide}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false)
            setEditingSlide(undefined)
          }}
          isLoading={isSubmitting}
        />
      ) : (
        <HeroSlideList
          heroSlides={heroSlides}
          isLoading={isLoading}
          onAddSlide={handleAddSlide}
          onEditSlide={handleEditSlide}
          onDeleteSlide={handleDeleteSlide}
          onToggleStatus={handleToggleStatus}
        />
      )}

      <ConfirmationDialog
        _open={!!slideToDelete}
        onOpenChange={(open) => !open && setSlideToDelete(null)}
        title="Delete Hero Slide"
        description={`Are you sure you want to delete the slide "${slideToDelete?.title}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        variant="destructive"
      />
    </div>
  )
}

export default HeroSlidesPage
